import { TFunction } from 'i18next';
import * as z from 'zod';
import countries from '../constants/countries.json';
import { SignUpFormField } from '../queries/marketplace';
import { createRegisterFieldsSchemas, FieldSchemaKey } from '../schemas/registerFieldsSchemas';
import type { SelectOption } from '../components/molecules/form-elements/FormSelectField';
import type { FieldDefinition } from '../components/shared/DynamicFormFields';

export const COUNTRY_OPTIONS = countries.map((country) => ({ value: country.cca2, label: country.name }));

export const GEO_LOCATION_FIELDS = ['country', 'city', 'region'] as const;

export const getRegisterSchema = (t: TFunction, signUpFormFields: SignUpFormField[] = []) => {
  const fieldSchemas = createRegisterFieldsSchemas(t);

  const schema = signUpFormFields.reduce(
    (acc, field) => {
      const fieldName = field.name as FieldSchemaKey;
      const fieldSchema = fieldSchemas[fieldName];

      if (fieldSchema) {
        acc[fieldName] = fieldSchema(field.required);
      }

      return acc;
    },
    {} as Partial<Record<FieldSchemaKey, z.ZodType<string | boolean | undefined, unknown>>>
  );

  return z.object(schema);
};

export type RegisterFormData = z.infer<ReturnType<typeof getRegisterSchema>>;

export type RegisterFormProps = {
  promoCode?: string | null;
};

type GetAvailableRegisterFieldsOptions = {
  t: TFunction;
  marketplaceCurrencyOptions: SelectOption[];
  prefilledPromoCode: string;
  isPromoLocked: boolean;
};

export const getAvailableRegisterFields = ({
  t,
  marketplaceCurrencyOptions,
  prefilledPromoCode,
  isPromoLocked,
}: GetAvailableRegisterFieldsOptions): FieldDefinition[] => [
  { name: 'firstName', type: 'text', label: t('register.firstName') },
  { name: 'lastName', type: 'text', label: t('register.lastName') },
  { name: 'email', type: 'text', label: t('register.email') },
  { name: 'phone', type: 'text', label: t('register.phone'), placeholder: '+XX XXX XXXX' },
  { name: 'password', type: 'password', label: t('register.password') },
  { name: 'address', type: 'text', label: t('register.address') },
  { name: 'city', type: 'text', label: t('register.city') },
  { name: 'country', type: 'select', label: t('register.country'), options: COUNTRY_OPTIONS },
  { name: 'region', type: 'text', label: t('register.region') },
  { name: 'zip', type: 'text', label: t('register.zip') },
  { name: 'currency', type: 'select', label: t('register.currency'), options: marketplaceCurrencyOptions },
  { name: 'dateOfBirth', type: 'date', label: t('dateOfBirth') },
  { name: 'ageVerified', type: 'checkbox', label: t('register.ageVerification') },
  {
    name: 'code',
    type: 'text',
    label: t('register.promoCode'),
    defaultValue: prefilledPromoCode,
    disabled: isPromoLocked,
  },
];
